<template>
    <div class="modal-card card agent-leave">
        <button type="button" class="btn-close agent-leave-close" data-bs-dismiss="modal" aria-label="Close"></button>

        <div class="card-body agent-leave-body">
            <div class="agent-leave-badge" aria-hidden="true">
                <i class="fa-solid fa-triangle-exclamation"></i>
            </div>

            <h3 class="agent-leave-title">Leave while a response is in progress?</h3>

            <div class="agent-leave-live">
                <span class="agent-leave-dot" aria-hidden="true"></span>
                <span class="agent-leave-agent">{{ modalData.agentName }}</span>
                <span class="agent-leave-sep" aria-hidden="true">·</span>
                <span>Generating response</span>
            </div>

            <ul class="agent-leave-points">
                <li v-if="modalData.canAbort" class="agent-leave-point-danger">
                    <i class="fa-solid fa-circle-stop" aria-hidden="true"></i>
                    <span>The response will be stopped and won't be completed.</span>
                </li>
                <li v-else class="agent-leave-point-danger">
                    <i class="fa-solid fa-eye-slash" aria-hidden="true"></i>
                    <span>You won't see the result on this page once you leave.</span>
                </li>
                <li>
                    <i class="fa-solid fa-hourglass-half" aria-hidden="true"></i>
                    <span>Stay on this page until it finishes to keep the full response.</span>
                </li>
            </ul>
        </div>

        <div class="agent-leave-footer">
            <button ref="leave" type="button" class="btn btn-outline-danger" @click="leaveClick">
                <i class="fa-solid fa-arrow-right-from-bracket me-1" aria-hidden="true"></i>
                Leave page
            </button>
            <button ref="stay" type="button" class="btn btn-primary" @click="stayClick">
                Stay on page
            </button>
        </div>
    </div>
</template>

<script>
/**
 * Shown by `confirmLeaveAgentStream()` (utils/agentStreamGuard.js) when the
 * user navigates away from an AI agent view mid-stream.
 *
 *   agentName   the agent that is streaming, e.g. 'PAICE'
 *   canAbort    true when leaving stops the stream; false when the request
 *               keeps running unseen (Map My Data, Graph My Data)
 *   confirm     called on "Leave page"
 *   cancel      called on "Stay on page"; dismissing via X/backdrop/Escape is
 *               handled by the guard itself
 */
export default {
    name: 'AgentStreamLeave',
    props: {
        modalData: {
            type: Object,
            default: () => ({}),
        },
    },
    mounted() {
        // Staying is the safe answer — Enter on reflex must not discard a response.
        this.$nextTick(() => this.$refs.stay?.focus());
    },
    methods: {
        stayClick() {
            if (typeof this.modalData?.cancel === 'function') this.modalData.cancel();
            this.$store.dispatch('hideModal');
        },
        leaveClick() {
            if (typeof this.modalData?.confirm === 'function') this.modalData.confirm();
            this.$store.dispatch('hideModal');
        },
    },
};
</script>

<style scoped>
.agent-leave {
    position: relative;
    margin-bottom: 0;
    overflow: hidden;
}

.agent-leave-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 1;
}

.agent-leave-body {
    padding: 2rem 2rem 1.5rem;
    text-align: center;
}

.agent-leave-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    margin-bottom: 1.1rem;
    border-radius: 50%;
    background: #fdf3e7;
    color: #d9822b;
    font-size: 1.35rem;
}

/* Soft halo so the badge reads as a caution, not an error. */
.agent-leave-badge::after {
    content: '';
    position: absolute;
    inset: -0.4rem;
    border-radius: 50%;
    border: 1px solid #fbe3c6;
}

.agent-leave-title {
    margin-bottom: 0.75rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #12263f;
}

.agent-leave-live {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    background: #e6f6ee;
    color: #00854a;
    font-size: 0.75rem;
    font-weight: 500;
}

.agent-leave-agent {
    font-weight: 700;
}

.agent-leave-sep {
    opacity: 0.6;
}

.agent-leave-dot {
    position: relative;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #00d97e;
}

.agent-leave-dot::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #00d97e;
    animation: agent-leave-pulse 1.6s ease-out infinite;
}

@keyframes agent-leave-pulse {
    from {
        transform: scale(1);
        opacity: 0.7;
    }
    to {
        transform: scale(2.6);
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .agent-leave-dot::after {
        animation: none;
    }
}

.agent-leave-points {
    list-style: none;
    margin: 1.4rem 0 0;
    padding: 0.85rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    text-align: left;
    border: 1px solid #edf2f9;
    border-radius: 0.5rem;
    background: #f9fbfd;
}

.agent-leave-points li {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    font-size: 0.8rem;
    line-height: 1.45;
    color: #6e84a3;
}

.agent-leave-points i {
    width: 1rem;
    margin-top: 0.15rem;
    flex-shrink: 0;
    text-align: center;
    font-size: 0.8rem;
    color: #95aac9;
}

.agent-leave-points .agent-leave-point-danger {
    color: #12263f;
}

.agent-leave-points .agent-leave-point-danger i {
    color: #e63757;
}

.agent-leave-footer {
    display: flex;
    gap: 0.75rem;
    padding: 1rem 2rem 1.75rem;
}

.agent-leave-footer .btn {
    flex: 1 1 0;
}

@media (max-width: 420px) {
    .agent-leave-footer {
        flex-direction: column-reverse;
    }
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` sits on an ancestor of the modal root. */
body.dark-mode {
    .agent-leave-title,
    .agent-leave-points .agent-leave-point-danger {
        color: $dark-text-primary !important;
    }

    .agent-leave-points {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .agent-leave-points li,
    .agent-leave-points i {
        color: $dark-text-secondary !important;
    }

    .agent-leave-points .agent-leave-point-danger i {
        color: #f5808f !important;
    }

    .agent-leave-badge {
        background: rgba(217, 130, 43, 0.16) !important;
        color: #e0a458 !important;
    }

    .agent-leave-badge::after {
        border-color: rgba(217, 130, 43, 0.25) !important;
    }

    .agent-leave-live {
        background: rgba(0, 217, 126, 0.14) !important;
        color: #5fd3a3 !important;
    }
}
</style>
